import React, {useReducer, forwardRef, useImperativeHandle} from 'react'
import {H6, IconButton, P, Row, Alert, Button, Modal, ModalActions} from '../UI/index.js'
import ChooseRoutePoint from './ChooseRoutePoint.js'
import {formSpacing, FormRow, FormStack, Input, Select, TimePicker, Toggle} from '../UI/forms/index.js'
import moment from 'moment'
import {Reseller} from '../../../server/functions/resellers/resellers.types.js'
import {Vehicle} from '../../../server/functions/vehicles/vehicles.types.js'
import {Route} from 'types/types.js'
import t from '../../helpers/Languages.js'
import {CreatePlanningOptions} from 'server/functions/orders/orders.js'

interface CreatePlanningPopupProps {
    reseller: Reseller
    routes: Route[]
    vehicles: Vehicle[]
}

interface CreatePlanningPopupState {
    isOpen: boolean
    confirm?: ((options: CreatePlanningOptions) => void)
    options: CreatePlanningOptions
    selectedOrders: string[]
    loading: boolean
    error: string
}

export interface CreatePlanningPopupRef {
    open: (selectedOrders: string[], confirm: (options: CreatePlanningOptions) => void) => void
    setError: (error: string) => void
}

const CreatePlanningPopup = forwardRef<CreatePlanningPopupRef, CreatePlanningPopupProps>((props, ref) => {
    const {reseller, routes = [], vehicles = []} = props

    const defaultVehicleType = reseller?.settings.vehicleTypes.find((v) => v._id === reseller.settings.defaultVehicle) || reseller?.settings.vehicleTypes[0]

    const newRoute: () => CreatePlanningOptions['routes'][0] = () => structuredClone({
        _id: '',
        plannedStartTime: '',
        vehicleType: defaultVehicleType,
        vehicleTypeId: defaultVehicleType?._id || '',
        vehicleId: '',
        maxDuration: 0,
        maxEndTime: '',
        quantity: 1,
        showOptions: false
    })

    const initialState: CreatePlanningPopupState = {
        isOpen: false,
        options: {
            plannedStartTime: '08:00',
            maxDuration: 480,
            maxEndTime: '',
            optimizeForTimeslots: !!reseller.settings.optimizeForTimeslots,
            optimizeForCapacity: false,
            routes: [
                newRoute(),
                newRoute()
            ]
        },
        selectedOrders: [],
        loading: false,
        error: ''
    }

    const [state, setState] = useReducer(
        (state: CreatePlanningPopupState, newState: Partial<CreatePlanningPopupState>) => ({...state, ...newState}),
        structuredClone(initialState)
    )

    useImperativeHandle(ref, () => ({open, close, setError}))

    const open = (selectedOrders: string[], confirm: (options: CreatePlanningOptions) => void) => {
        const options = localStorage.getItem('autoSetMessengerPopupOptions') ? JSON.parse(localStorage.getItem('autoSetMessengerPopupOptions')!) : {...state.options}
        setState({isOpen: true, confirm, options, selectedOrders})
    }

    const confirmAction = () => {
        const {options} = state

        // Add routes based on quantity
        const expandedRoutes: CreatePlanningOptions['routes'] = []

        options.routes.forEach((route) => {
            if (!route._id && !route.vehicleId) {
                for (let i = 0; i < (route.quantity || 1); i++) {
                    expandedRoutes.push({
                        _id: '',
                        plannedStartTime: route.plannedStartTime,
                        vehicleType: route.vehicleType,
                        vehicleTypeId: route.vehicleTypeId,
                        vehicleId: route.vehicleId,
                        startPoint: route.startPoint,
                        endPoint: route.endPoint,
                        maxDuration: route.maxDuration,
                        maxEndTime: route.maxEndTime
                    })
                }
            } else {
                expandedRoutes.push(route)
            }
        })

        const updatedOptions = {...options, routes: expandedRoutes}

        setState({loading: true, error: ''})

        if (state.confirm) {
            state.confirm(updatedOptions)
        }
    }

    const close = () => {
        state.options.routes = state.options.routes.map((route) => {
            if (route._id) {
                delete route._id
            }

            if (route.plannedStartTime === state.options.plannedStartTime) {
                delete route.plannedStartTime
            }

            if (route.maxEndTime === state.options.maxEndTime) {
                delete route.maxEndTime
            }

            if (route.maxDuration === state.options.maxDuration) {
                route.maxDuration = 0
            }

            return route
        })

        const nrOfRoutes = state.options.routes.reduce((acc, route) => {
            acc += route.quantity || 1

            return acc
        }, 0)

        if (nrOfRoutes === 0) {
            state.options.routes.push(newRoute())
        }

        localStorage.setItem('autoSetMessengerPopupOptions', JSON.stringify(state.options))
        setState(structuredClone(initialState))
    }

    const setError = (error: string) => {
        setState({error, loading: false})
    }

    const addRoute = () => {
        const options = {...state.options}

        options.routes.push(newRoute())

        setState({options})
    }

    const removeRoute = (index: number) => {
        const options = {...state.options}
        if (options.routes.length > 1) {
            options.routes.splice(index, 1)
        }
        setState({options})
    }

    const changeRoute = (index: number, event: React.ChangeEvent<HTMLInputElement>) => {
        const options = {...state.options}

        const id = event.target.value

        if (!id) {
            options.routes[index] = newRoute()
        } else {
            const route = routes.find((r) => r._id === event.target.value)

            if (!route) {
                return
            }

            const vehicleType = reseller.settings.vehicleTypes.find((vT) => vT._id === route.vehicleTypeId) || reseller.settings.vehicleTypes[0]

            options.routes[index]._id = route._id || ''
            options.routes[index].plannedStartTime = route.plannedStartTime || ''
            options.routes[index].maxDuration = route.maxDuration || 0
            options.routes[index].maxEndTime = route.maxEndTime || ''
            options.routes[index].vehicleType = vehicleType
            options.routes[index].vehicleTypeId = route.vehicleTypeId || ''
            options.routes[index].vehicleId = route.vehicleId
            options.routes[index].startPoint = route.startPoint
            options.routes[index].endPoint = route.endPoint
            options.routes[index].quantity = 1
            options.routes[index].showOptions = false
        }

        setState({options})
    }

    const changeVehicle = (index: number, event: React.ChangeEvent<HTMLInputElement>) => {
        const options = {...state.options}

        const vehicleType = reseller.settings.vehicleTypes.find((vT) => vT._id === event.target.value)

        if (!vehicleType) {
            return
        }

        options.routes[index].vehicleTypeId = vehicleType._id || ''
        options.routes[index].vehicleType = vehicleType

        const vehicle = vehicles.find((v) => v._id === (event.target as unknown as {subvalue: string}).subvalue)

        if (vehicle) {
            options.routes[index].vehicleId = vehicle._id || ''
        } else {
            options.routes[index].vehicleId = ''
        }

        setState({options})
    }

    const {isOpen, options, loading, error} = state

    let inputInvalid = false
    let maxEndTimeInvalid = false

    if (options.maxEndTime && options.maxEndTime < options.plannedStartTime) {
        maxEndTimeInvalid = true
        inputInvalid = true
    }

    return (
        <Modal show={isOpen} style={{width: 600}} onClose={() => close()} title='Automatisch routes berekenen'>
            <P style={{marginBottom: 24}}>Routes worden berekend op een zo laag mogelijke totale duur. Kies hieronder waar de route berekening aan moet voldoen. Het ingestelde bereik van de voertuigen kan nooit overschreden worden.</P>

            <FormStack style={{marginBottom: formSpacing.rowGap}}>
                <FormRow>
                    <TimePicker
                        label='Starttijd routes vanaf'
                        value={options.plannedStartTime}
                        onChange={(event) => setState({options: {...options, plannedStartTime: event.target.value || ''}})}
                    />

                    <TimePicker
                        allowEmpty
                        label='Eindtijd routes'
                        value={options.maxEndTime}
                        isInvalid={maxEndTimeInvalid}
                        onChange={(event) => setState({options: {...options, maxEndTime: event.target.value || ''}})}
                    />
                </FormRow>

                <TimePicker
                    duration={true}
                    label='Maximale routeduur'
                    value={options.maxDuration}
                    onChange={(event) => setState({options: {...options, maxDuration: parseInt(event.target.value) || 0}})}
                />
            </FormStack>

            <H6 style={{marginBottom: 12, marginTop: 24}}>Optimalisatie mogelijkheden</H6>

            <FormStack style={{marginBottom: formSpacing.rowGap}}>
                <FormRow>
                    <Toggle
                        label='Tijdvakken'
                        info='Als deze optie aan staat houdt de routeplanner rekening met tijdvakken van adressen. Routes kunnen een starttijd krijgen die later is dan de ingevulde starttijd.'
                        checked={options.optimizeForTimeslots}
                        onChange={(event) => setState({options: {...options, optimizeForTimeslots: event.target.checked}})}
                    />
                    <div style={{flex: 1}} />
                </FormRow>

                <FormRow>
                    <Toggle
                        label='Capaciteit'
                        info='Als deze optie is aangevinkt houdt de routeplanner rekening met de capaciteit van voertuigen.'
                        checked={options.optimizeForCapacity}
                        onChange={(event) => setState({options: {...options, optimizeForCapacity: event.target.checked}})}
                    />
                    <div style={{flex: 1}} />
                </FormRow>
            </FormStack>

            <H6 style={{marginTop: 24, marginBottom: 24}}>Routes</H6>
            {options.routes.map((route, index) => {
                const maxRouteEndTimeInvalid = route.maxEndTime && route.maxEndTime < (route.plannedStartTime || options.plannedStartTime)

                if (maxRouteEndTimeInvalid) {
                    inputInvalid = true
                }

                return (
                    <div key={index}>
                        <FormRow style={{alignItems: 'center', marginBottom: formSpacing.rowGap}}>
                            <IconButton
                                onClick={() => setState({options: {...options, routes: [...options.routes.slice(0, index), {...route, showOptions: !route.showOptions}, ...options.routes.slice(index + 1)]}})}
                                icon={route.showOptions ? 'mdi mdi-chevron-down' : 'mdi mdi-chevron-right'}
                            />

                            <Select
                                label={t('route')}
                                options={[
                                    {
                                        value: '',
                                        title: t('newRoute')
                                    },
                                    ...routes.filter((r1) => !options.routes.find((r2) => route._id !== r1._id && r1._id === r2._id)).map((route) => {
                                        return {
                                            value: route._id,
                                            title: route.name,
                                            icon: 'mdi mdi-circle',
                                            iconColor: route.color
                                        }
                                    })
                                ]}
                                value={route._id}
                                onChange={(event) => changeRoute(index, event)}
                            />

                            <FormRow style={{flex: 1}}>
                                <Select
                                    label={t('vehicle')}
                                    options={[
                                        ...reseller.settings.vehicleTypes.filter((vT) => !vT.isArchived).map((vehicleType) => ({
                                            value: vehicleType._id,
                                            title: vehicleType.name,
                                            icon: `mdi mdi-${vehicleType.icon}`
                                        })),
                                        ...vehicles.filter((v) => !v.isArchived).sort((a, b) => {
                                            const unavailable = b.notAvailableDateTimeRanges?.find((range) => moment().isBetween(range.start, range.end, 'day', '[]'))

                                            return unavailable ? -9999 : 0
                                        }).map((vehicle) => {
                                            const activeVehicleType = reseller.settings.vehicleTypes.find((type) => type._id === vehicle.typeId)

                                            if (!activeVehicleType) {
                                                return
                                            }

                                            return {
                                                value: activeVehicleType?._id,
                                                subvalue: vehicle._id,
                                                title: vehicle.name,
                                                icon: `mdi mdi-${activeVehicleType.icon}`,
                                                disabled: vehicle.notAvailableDateTimeRanges?.find((range) => moment().isBetween(range.start, range.end, 'day', '[]'))
                                            }
                                        })
                                    ]}
                                    value={route.vehicleTypeId}
                                    subvalue={route.vehicleId}
                                    onChange={(event) => changeVehicle(index, event)}
                                    groupSubvalues
                                />
                                {!route._id && !route.vehicleId &&
                                    <Input
                                        style={{width: 60}}
                                        label='Aantal'
                                        type='number'
                                        min={1}
                                        value={route.quantity}
                                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                            const options = {...state.options}
                                            options.routes[index].quantity = Math.max(1, parseInt(event.target.value) || 1)
                                            setState({options})
                                        }}
                                    />
                                }
                            </FormRow>

                            <div style={{width: 36}}>
                                {options.routes.length > 1 &&
                                    <IconButton
                                        onClick={() => removeRoute(index)}
                                        icon='mdi mdi-delete'
                                    />
                                }
                            </div>
                        </FormRow>

                        {route.showOptions &&
                            <FormStack style={{paddingLeft: formSpacing.section * 2, paddingRight: formSpacing.section * 2, marginBottom: formSpacing.rowGap}}>
                                <ChooseRoutePoint
                                    label='Start:'
                                    dropdownLabel='Startpunt'
                                    address={route.startPoint}
                                    reseller={reseller}
                                    onChange={(startPoint: Route['startPoint']) => {
                                        const options = {...state.options}
                                        options.routes[index].startPoint = startPoint
                                        setState({options})
                                    }}
                                />

                                <ChooseRoutePoint
                                    label='Eind:'
                                    dropdownLabel='Eindpunt'
                                    address={route.endPoint}
                                    reseller={reseller}
                                    onChange={(endPoint: Route['endPoint']) => {
                                        const options = {...state.options}
                                        options.routes[index].endPoint = endPoint
                                        setState({options})
                                    }}
                                />

                                <FormRow>
                                    <TimePicker
                                        label='Starttijd route vanaf'
                                        value={route.plannedStartTime}
                                        placeholder={options.plannedStartTime}
                                        allowEmpty={true}
                                        onChange={(event) => {
                                            const options = {...state.options}
                                            options.routes[index].plannedStartTime = event.target.value
                                            setState({options})
                                        }}
                                    />
                                    <TimePicker
                                        label='Eindtijd route'
                                        value={route.maxEndTime}
                                        isInvalid={maxRouteEndTimeInvalid}
                                        placeholder={options.maxEndTime}
                                        allowEmpty={true}
                                        onChange={(event) => {
                                            const options = {...state.options}
                                            options.routes[index].maxEndTime = event.target.value
                                            setState({options})
                                        }}
                                    />
                                </FormRow>

                                <TimePicker
                                    label='Maximale routeduur'
                                    duration={true}
                                    allowEmpty={true}
                                    value={route.maxDuration}
                                    placeholder={options.maxDuration}
                                    onChange={(event) => {
                                        const options = {...state.options}
                                        options.routes[index].maxDuration = parseInt(event.target.value) || 0
                                        setState({options})
                                    }}
                                />
                            </FormStack>
                        }
                    </div>
                )
            })}

            <Row style={{marginBottom: 32}}>
                <Button
                    style={{width: '100%'}}
                    variant='outline-white'
                    onClick={() => addRoute()}
                    label='Route toevoegen'
                    icon='mdi mdi-plus'
                />
            </Row>

            {error &&
                <Alert style={{marginBottom: 6}} variant='danger'>{error}</Alert>
            }

            <ModalActions>
                <Button
                    variant='text'
                    disabled={loading}
                    onClick={() => close()}
                >
                    Annuleren
                </Button>

                <Button
                    variant='text'
                    loading={loading}
                    disabled={inputInvalid}
                    onClick={() => confirmAction()}
                >
                    Bevestigen
                </Button>
            </ModalActions>
        </Modal>
    )
})

CreatePlanningPopup.displayName = 'CreatePlanningPopup'

export default CreatePlanningPopup
